import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Badge from "@material-ui/core/Badge";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import { i18n } from "../../translate/i18n";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
} from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  tabmsg: {
    backgroundColor: theme.palette.campaigntab,
  },

  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  tabDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    display: "inline-block",
    marginLeft: 4,
    verticalAlign: "middle",
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },

  readonlyBanner: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    padding: theme.spacing(0.75, 2),
    fontSize: "0.8rem",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const STATUS_CONFIG = {
  INATIVA:      { label: "Inativa",       color: "#9e9e9e", bg: "#f5f5f5" },
  PROGRAMADA:   { label: "Programada",    color: "#1565c0", bg: "#e3f2fd" },
  EM_ANDAMENTO: { label: "Em Andamento",  color: "#2e7d32", bg: "#e8f5e9" },
  CANCELADA:    { label: "Cancelada",     color: "#c62828", bg: "#ffebee" },
  FINALIZADA:   { label: "Finalizada",    color: "#6a1b9a", bg: "#f3e5f5" },
};

const CampaignSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
});

const CampaignModal = ({
  open,
  onClose,
  campaignId,
  initialValues,
  onSave,
  resetPagination,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const { companyId } = user;
  const [file, setFile] = useState(null);

  const initialState = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    confirmationMessage1: "",
    confirmationMessage2: "",
    confirmationMessage3: "",
    confirmationMessage4: "",
    confirmationMessage5: "",
    status: "INATIVA",
    confirmation: false,
    scheduledAt: "",
    whatsappId: "",
    contactListId: "",
    tagListId: "",
    companyId,
  };

  const [campaign, setCampaign] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [messageTab, setMessageTab] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const attachmentFile = useRef(null);
  const [tagLists, setTagLists] = useState([]);
  // tagListId fora do Formik para evitar inferência automática de valor
  const [selectedTagId, setSelectedTagId] = useState(null);
  // modo de público: "list" = lista de contatos | "tag" = filtrar por tag
  const [audienceMode, setAudienceMode] = useState("list");

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/files/", { params: { companyId } });
        setFile(data.files);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      if (initialValues) {
        setCampaign((prev) => ({ ...prev, ...initialValues }));
      }

      api.get(`/contact-lists/list`, { params: { companyId } })
        .then(({ data }) => setContactLists(data));

      api.get(`/whatsapp`, { params: { companyId, session: 0 } })
        .then(({ data }) => setWhatsapps(data));

      api.get(`/tags`, { params: { companyId } })
        .then(({ data }) => {
          setTagLists(data.tags.map((t) => ({ id: t.id, name: t.name })));
        })
        .catch((err) => console.error("Error retrieving tags:", err));

      if (!campaignId) {
        setSelectedTagId(null);
        return;
      }

      api.get(`/campaigns/${campaignId}`).then(({ data }) => {
        // carrega tagListId no estado próprio, fora do Formik
        const tagId = data.tagListId;
        const parsedTagId = tagId ? parseInt(tagId, 10) : null;
        setSelectedTagId(parsedTagId);
        setAudienceMode(parsedTagId ? "tag" : "list");

        setCampaign((prev) => {
          const next = { ...prev };
          Object.entries(data).forEach(([key, value]) => {
            if (key === "tagListId") return; // gerenciado pelo estado próprio
            if (key === "scheduledAt" && value) {
              next[key] = moment(value).format("YYYY-MM-DDTHH:mm");
            } else {
              next[key] = value === null ? "" : value;
            }
          });
          return next;
        });
      });
    }
  }, [campaignId, open, initialValues, companyId]);

  useEffect(() => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    const moreThenAnHour =
      !Number.isNaN(scheduledAt.diff(now)) && scheduledAt.diff(now, "hour") > 1;
    const isEditable =
      campaign.status === "INATIVA" ||
      (campaign.status === "PROGRAMADA" && moreThenAnHour);
    setCampaignEditable(isEditable);
  }, [campaign.status, campaign.scheduledAt]);

  const handleClose = () => {
    onClose();
    setCampaign(initialState);
    setSelectedTagId(null);
    setAudienceMode("list");
  };

  const handleAttachmentFile = (e) => {
    const f = head(e.target.files);
    if (f) setAttachment(f);
  };

  const handleSaveCampaign = async (values) => {
    try {
      const dataValues = {};
      Object.entries(values).forEach(([key, value]) => {
        if (key === "scheduledAt" && value) {
          dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
        } else if (key === "tagListId") {
          // ignorado aqui — lido do estado próprio abaixo
        } else if (key === "contactListId" || key === "whatsappId" || key === "fileListId") {
          const parsed = parseInt(value, 10);
          dataValues[key] = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        } else {
          dataValues[key] = value === "" ? null : value;
        }
      });
      // tagListId vem exclusivamente do estado próprio, nunca do Formik
      dataValues.tagListId = selectedTagId || null;

      if (campaignId) {
        await api.put(`/campaigns/${campaignId}`, dataValues);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${campaignId}/media-upload`, formData);
        }
        handleClose();
      } else {
        const { data } = await api.post("/campaigns", dataValues);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${data.id}/media-upload`, formData);
        }
        if (onSave) onSave(data);
        handleClose();
      }
      toast.success(i18n.t("campaigns.toasts.success"));
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }
    if (campaign.mediaPath) {
      await api.delete(`/campaigns/${campaign.id}/media-upload`);
      setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Retorna label da aba com indicador de preenchimento
  const tabLabel = (index, values) => {
    const key = `message${index + 1}`;
    const filled = values[key] && values[key].trim().length > 0;
    return (
      <span>
        Msg. {index + 1}
        {filled && <span className={classes.tabDot} />}
      </span>
    );
  };

  const renderMessageField = (identifier, disabled) => (
    <Field
      as={TextField}
      id={identifier}
      name={identifier}
      fullWidth
      rows={5}
      label={i18n.t(`campaigns.dialog.form.${identifier}`)}
      placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
      multiline
      variant="outlined"
      helperText="Utilize variáveis como {nome}, {numero}, {email} ou variáveis personalizadas."
      disabled={disabled}
    />
  );

  const renderConfirmationMessageField = (identifier, disabled) => (
    <Field
      as={TextField}
      id={identifier}
      name={identifier}
      fullWidth
      rows={5}
      label={i18n.t(`campaigns.dialog.form.${identifier}`)}
      placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
      multiline
      variant="outlined"
      disabled={disabled}
    />
  );

  const statusCfg = STATUS_CONFIG[campaign.status] || {};

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
        {/* Cabeçalho */}
        <DialogTitle style={{ paddingBottom: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
            <span>
              {campaignEditable
                ? campaignId
                  ? i18n.t("campaigns.dialog.update")
                  : i18n.t("campaigns.dialog.new")
                : i18n.t("campaigns.dialog.readonly")}
            </span>
            {campaignId && campaign.status && (
              <Chip
                size="small"
                label={statusCfg.label}
                style={{
                  backgroundColor: statusCfg.bg,
                  color: statusCfg.color,
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  height: 22,
                }}
              />
            )}
          </Box>
        </DialogTitle>

        {/* Aviso de somente leitura */}
        {!campaignEditable && (
          <Typography className={classes.readonlyBanner}>
            Esta campanha está em modo somente leitura.
            {campaign.status === "PROGRAMADA" && " Para editar, cancele o agendamento ou edite com mais de 1h de antecedência."}
          </Typography>
        )}

        <div style={{ display: "none" }}>
          <input type="file" ref={attachmentFile} onChange={handleAttachmentFile} />
        </div>

        <Formik
          initialValues={campaign}
          enableReinitialize
          validationSchema={CampaignSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaign(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid spacing={2} container>
                  {/* Nome */}
                  <Grid xs={12} md={8} item>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.name")}
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>

                  {/* Confirmação — Switch */}
                  <Grid xs={12} md={4} item style={{ display: "flex", alignItems: "center" }}>
                    <Tooltip title="Quando habilitada, cada mensagem pode ter uma versão de confirmação de leitura">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(values.confirmation)}
                            onChange={(e) => setFieldValue("confirmation", e.target.checked)}
                            color="primary"
                            disabled={!campaignEditable}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {i18n.t("campaigns.dialog.form.confirmation")}
                          </Typography>
                        }
                      />
                    </Tooltip>
                  </Grid>

                  {/* Público-alvo — lista OU tag (mutuamente exclusivos) */}
                  <Grid xs={12} item>
                    <Box
                      style={{
                        border: `1px solid rgba(0,0,0,0.23)`,
                        borderRadius: 4,
                        padding: "8px 16px 12px",
                        marginTop: 4,
                      }}
                    >
                      {/* Título da seção com help */}
                      <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 8 }}>
                        <Typography variant="caption" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "inherit" }}>
                          Público-alvo
                        </Typography>
                        <Tooltip
                          arrow
                          placement="right"
                          title={
                            <Box style={{ fontSize: "0.8rem", lineHeight: 1.6, maxWidth: 280 }}>
                              <strong>Lista de contatos:</strong> usa uma lista criada manualmente em "Listas de Contatos". Ideal para bases fixas.<br /><br />
                              <strong>Filtrar por tag:</strong> busca automaticamente todos os contatos que possuem tickets marcados com a tag escolhida. A lista é gerada no momento do envio.
                            </Box>
                          }
                        >
                          <HelpOutlineIcon style={{ fontSize: 15, color: "rgba(0,0,0,0.4)", cursor: "help" }} />
                        </Tooltip>
                      </Box>

                      {/* Seleção do modo via radio */}
                      <RadioGroup
                        row
                        value={audienceMode}
                        onChange={(e) => {
                          const mode = e.target.value;
                          setAudienceMode(mode);
                          if (mode === "list") setSelectedTagId(null);
                          if (mode === "tag") setFieldValue("contactListId", "");
                        }}
                      >
                        <FormControlLabel
                          value="list"
                          control={<Radio color="primary" size="small" disabled={!campaignEditable} />}
                          label={<Typography variant="body2">Lista de contatos</Typography>}
                        />
                        <FormControlLabel
                          value="tag"
                          control={<Radio color="primary" size="small" disabled={!campaignEditable} />}
                          label={<Typography variant="body2">Filtrar por tag</Typography>}
                        />
                      </RadioGroup>

                      {/* Campo condicional */}
                      {audienceMode === "list" ? (
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel id="contactList-selection-label">
                            {i18n.t("campaigns.dialog.form.contactList")}
                          </InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t("campaigns.dialog.form.contactList")}
                            labelId="contactList-selection-label"
                            id="contactListId"
                            name="contactListId"
                            disabled={!campaignEditable}
                          >
                            <MenuItem value="">Nenhuma</MenuItem>
                            {contactLists && contactLists.map((cl) => (
                              <MenuItem key={cl.id} value={String(cl.id)}>{cl.name}</MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      ) : (
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel id="tagList-selection-label">
                            {i18n.t("campaigns.dialog.form.tagList")}
                          </InputLabel>
                          <Select
                            label={i18n.t("campaigns.dialog.form.tagList")}
                            labelId="tagList-selection-label"
                            id="tagListId"
                            value={selectedTagId && selectedTagId > 0 ? String(selectedTagId) : ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSelectedTagId(v ? parseInt(v, 10) : null);
                            }}
                            disabled={!campaignEditable}
                          >
                            <MenuItem value="">Selecione uma tag</MenuItem>
                            {Array.isArray(tagLists) && tagLists.map((t) => (
                              <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Grid>

                  {/* Conexão WhatsApp */}
                  <Grid xs={12} md={4} item>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="whatsapp-selection-label">
                        {i18n.t("campaigns.dialog.form.whatsapp")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.whatsapp")}
                        labelId="whatsapp-selection-label"
                        id="whatsappId"
                        name="whatsappId"
                        disabled={!campaignEditable}
                      >
                        <MenuItem value="">Nenhuma</MenuItem>
                        {whatsapps && whatsapps.map((w) => (
                          <MenuItem key={w.id} value={String(w.id)}>{w.name}</MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Agendamento */}
                  <Grid xs={12} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.scheduledAt")}
                      name="scheduledAt"
                      error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                      helperText={
                        touched.scheduledAt && errors.scheduledAt
                          ? errors.scheduledAt
                          : "Deixe vazio para criar como inativa"
                      }
                      variant="outlined"
                      margin="dense"
                      type="datetime-local"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>

                  {/* Arquivo de mídia */}
                  <Grid xs={12} md={4} item>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="fileListId-selection-label">
                        {i18n.t("campaigns.dialog.form.fileList")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.fileList")}
                        name="fileListId"
                        id="fileListId"
                        labelId="fileListId-selection-label"
                        value={values.fileListId || ""}
                      >
                        <MenuItem value="">Nenhum</MenuItem>
                        {file && file.map((f) => (
                          <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Abas de mensagens */}
                  <Grid xs={12} item>
                    <Tabs
                      value={messageTab}
                      indicatorColor="primary"
                      textColor="primary"
                      className={classes.tabmsg}
                      onChange={(e, v) => setMessageTab(v)}
                      variant="fullWidth"
                      centered
                      style={{ borderRadius: 2 }}
                    >
                      {[0, 1, 2, 3, 4].map((i) => (
                        <Tab key={i} label={tabLabel(i, values)} index={i} />
                      ))}
                    </Tabs>

                    <Box style={{ paddingTop: 20 }}>
                      {[0, 1, 2, 3, 4].map((i) => {
                        if (messageTab !== i) return null;
                        const msgKey = `message${i + 1}`;
                        const confKey = `confirmationMessage${i + 1}`;
                        const disabled = !campaignEditable && campaign.status !== "CANCELADA";
                        return (
                          <div key={i}>
                            {values.confirmation ? (
                              <Grid spacing={2} container>
                                <Grid xs={12} md={8} item>
                                  {renderMessageField(msgKey, disabled)}
                                </Grid>
                                <Grid xs={12} md={4} item>
                                  {renderConfirmationMessageField(confKey, disabled)}
                                </Grid>
                              </Grid>
                            ) : (
                              renderMessageField(msgKey, disabled)
                            )}
                          </div>
                        );
                      })}
                    </Box>
                  </Grid>

                  {/* Anexo atual */}
                  {(campaign.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <Button startIcon={<AttachFileIcon />} size="small" variant="outlined">
                        {attachment != null ? attachment.name : campaign.mediaName}
                      </Button>
                      {campaignEditable && (
                        <Tooltip title="Remover arquivo">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmationOpen(true)}
                            color="secondary"
                            style={{ marginLeft: 4 }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              {/* Rodapé com hierarquia clara */}
              <DialogActions style={{ justifyContent: "space-between", padding: "12px 16px" }}>
                {/* Ações de controle da campanha (esquerda) */}
                <Box display="flex" style={{ gap: 8 }}>
                  {campaign.status === "EM_ANDAMENTO" && (
                    <Button color="default" onClick={cancelCampaign} variant="outlined" size="small">
                      {i18n.t("campaigns.dialog.buttons.cancel")}
                    </Button>
                  )}
                  {campaign.status === "CANCELADA" && (
                    <Button color="primary" onClick={restartCampaign} variant="outlined" size="small">
                      {i18n.t("campaigns.dialog.buttons.restart")}
                    </Button>
                  )}
                  {!attachment && !campaign.mediaPath && campaignEditable && (
                    <Button
                      color="primary"
                      onClick={() => attachmentFile.current.click()}
                      disabled={isSubmitting}
                      variant="outlined"
                      size="small"
                      startIcon={<AttachFileIcon />}
                    >
                      {i18n.t("campaigns.dialog.buttons.attach")}
                    </Button>
                  )}
                </Box>

                {/* Fechar + Salvar (direita) */}
                <Box display="flex" style={{ gap: 8 }}>
                  <Button
                    onClick={handleClose}
                    color="default"
                    disabled={isSubmitting}
                    variant="outlined"
                    size="small"
                  >
                    {i18n.t("campaigns.dialog.buttons.close")}
                  </Button>
                  {(campaignEditable || campaign.status === "CANCELADA") && (
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                      variant="contained"
                      size="small"
                      className={classes.btnWrapper}
                    >
                      {campaignId
                        ? i18n.t("campaigns.dialog.buttons.edit")
                        : i18n.t("campaigns.dialog.buttons.add")}
                      {isSubmitting && (
                        <CircularProgress size={24} className={classes.buttonProgress} />
                      )}
                    </Button>
                  )}
                </Box>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CampaignModal;
